import { useRef, useState } from 'react';
import { Text, TextBlock } from '../../Atoms/Text';
import { format, parseISO } from 'date-fns';
import { Comment, useCommentsStore } from '../../../store/Comment';
import { getUser, isAdmin, isSupport } from '../../../store/User';
import { TextEditor } from './TextEditor';
import { sanitizeCommentHtml } from './commentHtml';
import { useHtmlPreview } from '../../../utils/htmlPreview';
import { MentionableUser } from '../../../types';

export interface CommentItemProps {
  comment: Comment;
  handleReply: (comment: Comment) => void;
  mentionableUsers: MentionableUser[];
  canReply?: boolean;
}

export function CommentItem({ comment, handleReply, mentionableUsers, canReply = true }: CommentItemProps) {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Set before the request goes, so a second Send or Ctrl+Enter in the same moment finds the save in flight.
  const savingRef = useRef<boolean>(false);
  // The text the editor opened on, so a re-read of the thread cannot reset what is being typed.
  const [editedText, setEditedText] = useState<string>('');
  const { archiveComment, updateComment } = useCommentsStore();
  const user = getUser();
  const isOwner = String(comment.portal_user_id) == user.id;
  const { html, isLong, content } = useHtmlPreview(comment.comment_text, sanitizeCommentHtml, showMore);

  const isArchivable = () => {
    return !comment.is_archived && (isOwner || isAdmin(user) || isSupport(user));
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    // A refused archive leaves the comment as it was, so its buttons work again and an open edit keeps its text.
    if (await archiveComment(comment)) setIsEditing(false);
    else setIsArchiving(false);
  };

  const handleEdit = () => {
    // The editor writes its text into the page as markup, where an event handler in it would run.
    setEditedText(html);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleUpdateComment = async (comment_text: string) => {
    // A deleted comment can no longer be changed; its editor stays only so the text can be copied.
    if (savingRef.current || comment.is_archived) return;
    savingRef.current = true;
    setIsSaving(true);
    const saved = await updateComment({ ...comment, comment_text });
    savingRef.current = false;
    setIsSaving(false);
    // A refused edit keeps the editor open with its text, so nothing typed is lost.
    if (saved) setIsEditing(false);
  };

  return (
    <div
      id={comment.id}
      className={`border-b border-grey-3 mb-4 pb-4 ${comment.parent_comment_id ? 'ml-6' : ''}`}>
      <div className="flex gap-2">
        {!!comment.portal_user_name && (
          <TextBlock
            size="sm"
            weight="medium">
            {comment.portal_user_name}
          </TextBlock>
        )}
        {comment.created_at && (
          <TextBlock
            size="sm"
            color="grey6">
            {format(parseISO(comment.created_at), 'PPP p')}
          </TextBlock>
        )}
        {comment.text_changed_at && (
          <TextBlock
            size="sm"
            color="grey6">
            (edited)
          </TextBlock>
        )}
      </div>
      <div className={isEditing ? 'mt-4' : ''}>
        {(!isEditing || comment.is_archived) && <div className="text-black text-sm ql-editor pl-0">{content}</div>}
        {isEditing && (
          <TextEditor
            commentText={editedText}
            handleCancel={handleCancelEdit}
            handleSend={handleUpdateComment}
            portalUsers={mentionableUsers}
            placeholder="Comment here"
            sendDisabled={isSaving || comment.is_archived}
          />
        )}
      </div>
      <div className="flex gap-4 mt-4">
        {!isEditing && isLong && (
          <button
            aria-expanded={showMore}
            onClick={() => setShowMore(!showMore)}>
            <Text
              size="sm"
              color="indigoMid">
              {showMore ? 'Show Less' : 'Show More'}
            </Text>
          </button>
        )}
        {isArchivable() && (
          <button
            disabled={isArchiving}
            onClick={() => handleArchive()}>
            <Text
              size="sm"
              color="indigoMid">
              Delete
            </Text>
          </button>
        )}
        {canReply && (
          <button onClick={() => handleReply(comment)}>
            <Text
              size="sm"
              color="indigoMid">
              Reply
            </Text>
          </button>
        )}
        {!comment.is_archived && isOwner && (
          <button
            disabled={isArchiving}
            onClick={() => handleEdit()}>
            <Text
              color="indigoMid"
              size="sm">
              Edit
            </Text>
          </button>
        )}
      </div>
    </div>
  );
}
